import { useContext, useEffect, useRef, useState } from 'react';
import { DateShort } from '@medable/i18n-core';
import { LocaleContext } from '../../../i18n/src';
import { toZonedDateTime } from './date_utils';
/**
 * Custom React hook to parse and format dates using input elements and a calendar library.
 * @param value - The date value for the date picker
 * @returns An object containing the input value, calendar value, change handlers, and error state
 */
export const useDatePicker = ({ value }) => {
    const locale = useContext(LocaleContext).toString();
    // Formats the date for the input element
    const formatDate = (date) => new DateShort(locale).format(toZonedDateTime(date));
    // Parses the date from a string input
    const parseDate = (input) => new DateShort(locale).parse(input);
    const currentValueRef = useRef(value);
    // State variables for input value, calendar value, and error
    const [inputValue, setInputValue] = useState(value ? formatDate(value) : '');
    const [calendarValue, setCalendarValue] = useState(value);
    const [error, setError] = useState();
    useEffect(() => {
        const formattedNewValue = value ? formatDate(value) : '';
        const formattedRefValue = currentValueRef.current ? formatDate(currentValueRef.current) : '';
        if (formattedRefValue !== formattedNewValue) {
            setInputValue(formattedNewValue);
            setCalendarValue(value);
            currentValueRef.current = value;
        }
    }, [value]);
    /**
     * Handles changes in the input element.
     * Parses the input value and sets the error state if the input isn't formatted correctly.
     */
    const inputOnChange = (event) => {
        try {
            const inputValue = event.target.value;
            // Keep track of input value to always display it to users, even if it's not valid
            setInputValue(inputValue);
            // Parse the input value to make sure it matches the expected format
            const plainDate = inputValue ? parseDate(inputValue) : undefined;
            // Clear out the error, as the date string was parsed correctly
            setError(undefined);
            // Set the state value
            setCalendarValue(plainDate);
        }
        catch (error) {
            // Set the error, so the input can display it
            setError(error);
            // Set the state value to undefined, as the input value is not valid
            setCalendarValue(undefined);
        }
    };
    /**
     * Handles changes in the calendar element.
     * Resets the error state and updates the input value according to the selected date.
     */
    const calendarOnChange = (plainDate) => {
        // Clear out the error, as any date coming from the calendar is valid
        setError(undefined);
        // Update the input value with the incoming date
        setInputValue(plainDate ? formatDate(plainDate) : '');
        // Set the state value
        setCalendarValue(plainDate);
    };
    // Return the result object containing the input value, calendar value, change handlers, and error state
    return {
        inputValue,
        calendarValue,
        calendarOnChange,
        inputOnChange,
        error,
    };
};
//# sourceMappingURL=use_date_picker.js.map